import Layout from "../Layout/Layout";
import ImageGallery from "../../components/ImageGallery/ImageGallery";
import { FaTrailer } from "react-icons/fa6";
import { PiToilet, PiOven } from "react-icons/pi";
import { TbRating21Plus } from "react-icons/tb";
import {
  MdOutlineUpcoming,
  MdPets,
  MdOutlineBed,
  MdBatteryChargingFull,
  MdSunnySnowing,
} from "react-icons/md";
import { JsonLDData } from "react-safe-json-ld";
import { FAQItem } from "../../components/FAQs/FAQItem";
import FAQs from "../../components/FAQs/FAQs";
import BookingCard from "../../components/Quote/BookingCard";

const SerenityDetail = () => {
  const FAQData: FAQItem[] = [
    {
      question: "What is Serenity best suited for?",
      answer:
        "Calm, zen-style getaways focused on hiking, fishing, exploring, remote work, and peaceful boondocking.",
    },
    {
      question: "How many people can Serenity sleep?",
      answer: "Comfortably sleeps 3 - 4 adults.",
    },
    {
      question: "How long can Serenity stay off-grid?",
      answer:
        "Over a week off-grid with its large solar panels, batteries, and water supplies.",
    },
    {
      question: "Is the interior really zen and calm?",
      answer:
        "Yes. Serenity is intentionally designed with a peaceful, minimalist interior for relaxation and focus.",
    },
    {
      question: "Can Serenity tow a boat or trailer?",
      answer:
        "Yes, up to 10,000 lbs with its frame-mounted receiver on the Ford E450 chassis.",
    },
    {
      question: "Where can I pick up Serenity?",
      answer: "Pickup is in Richardson, Texas.",
    },
    {
      question: "Can I book Serenity now?",
      answer:
        "Not yet — she arrives November 2026. You can pre-book dates as interest. That is not a paid reservation. We’ll email when she is ready to rent.",
    },
  ];

  const images: string[] = [
    "https://www.earthboundmartian.com/images/fleet/serenity/serenity_rear_coating_complete.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-15 15.48.20.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-22 16.29.08.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-02-08 16.31.54.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-02-15 13.34.20.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-02-28 11.54.58.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-01-18 15.39.53.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2025-11-09 17.28.07.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2025-11-28 12.43.58.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-28 13.32.09.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-28 13.32.17.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-29 09.19.09.jpg",
    "https://www.earthboundmartian.com/images/fleet/serenity/2026-03-29 09.19.18.jpg",
  ];

  const structuredData: JsonLDData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        name: "Serenity Landship Camper Van",
        description:
          "Serenity is a rugged off-grid camper van on a Ford E450 chassis. Calm and zen interior sleeps 3-4 comfortably, with over a week of boondocking capability. Perfect for peaceful hiking, fishing, and remote work getaways within 350 miles of DFW. Can tow a boat or trailer.",
        brand: { "@type": "Brand", name: "Earthbound Martian" },
        image: images,
        offers: { "@type": "Offer", priceCurrency: "USD", price: "220" },
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQData.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <Layout structuredData={structuredData}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Image + Title */}
        <div className="relative rounded-3xl overflow-hidden mb-12">
          <img
            src="/images/fleet/serenity/2026-04-11 10.11.20.jpg"
            alt="Serenity"
            className="w-full h-[500px] object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-10 left-10 text-white">
            <h1 className="text-6xl font-bold">Serenity</h1>
            <p className="text-2xl mt-2">
              Rugged • Powerful • Luxurious | Arriving November 2026
            </p>
          </div>
        </div>

        {/* Quick Icons */}
        <div className="flex flex-wrap gap-6 mb-12">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineUpcoming size={24} />
            </span>
            <div>
              <div className="font-semibold">Coming Soon!</div>
              <div className="text-md text-zinc-500">Premiering November 2026</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdPets size={24} />
            </span>
            <div className="font-semibold">Pet Friendly</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbRating21Plus size={24} />
            </span>
            <div className="font-semibold">21+</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineBed size={24} />
            </span>
            <div>
              <div className="font-semibold">Queen + Twin</div>
              <div className="text-md text-zinc-500">Sleeps 3-4</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <FaTrailer size={24} />
            </span>
            <div>
              <div className="font-semibold">Towing</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xl text-zinc-600 leading-relaxed mb-10">
              A powerful 2011 Glaval E450 V10 bus conversion built to go
              anywhere in America.
              <br />
              <br />
              Rugged on the outside with serious towing power and presence.
              Elegant and luxuriously comfortable inside; captain’s chairs up
              front, a full wet bath with shower and toilet, a full kitchen with
              propane stove and oven, and on-demand hot water.
              <br />
              <br />
              The full-size sofa converts to a queen bed, the dinette becomes a
              twin, and large windows flood the space with light while thick
              spray foam insulation keeps it quiet and comfortable year-round.
              <br />
              <br />
              1500W solar + 550Wh battery keeps you powered for days off-grid.
              <br />
              <br />
              Serenity is your mobile home and office - rugged, powerful, and
              refined - ready to take you across the country in comfort.
            </p>

            <div className="text-xl mb-10 font-bold">
              ** All Features are aspirational at this time **
            </div>

            {/* Vehicle Info */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-6">Vehicle Info</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-sm">
                <div>
                  <span className="text-lg text-zinc-500">Length:</span> 26 ft
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Transmission:</span>{" "}
                  Automatic
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Fuel Type:</span> Gas
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Range:</span> ~ 400
                  miles
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Weight:</span> 15,000
                  lbs
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Sleeps:</span> 3-4
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-8">Amenities</h3>

              <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
                {/* Bathroom */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiToilet size={24} />
                    </span>{" "}
                    Bathroom
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Fully Enclosed Wet Shower</li>
                    <li>• Cassette Toilet</li>
                    <li>• Venting</li>
                    <li>• Tankless Water Heater</li>
                  </ul>
                </div>

                {/* Kitchen */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiOven size={24} />
                    </span>{" "}
                    Kitchen
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Propane oven and stove</li>
                    <li>• Sink with running water</li>
                    <li>• 10 cu ft Fridge</li>
                    <li>• 46 Gallon Fresh Water Tank</li>
                    <li>• Convertible Dinnette</li>
                  </ul>
                </div>

                {/* Power & Hookups */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdBatteryChargingFull />
                    </span>{" "}
                    Power & Hookups
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• 1500W Solar + 550Wh Battery</li>
                    <li>• Shore power hookup</li>
                    <li>• External Utility Plug</li>
                    <li>• Charge from Chassis</li>
                    <li>• Battery Boost</li>
                  </ul>
                </div>

                {/* Climate */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdSunnySnowing />{" "}
                    </span>{" "}
                    Climate & Comfort
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• 12k BTU Mini Split</li>
                    <li>• Propane Powered Heater</li>
                    <li>• 2" Insulation</li>
                    <li>• Thermal Curtains</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-24 h-fit">
            <BookingCard
              rig="serenity"
              heading="Arriving November 2026"
              prebook
              availableFrom="2026-11-01"
            />
          </div>
        </div>

        {/* Full Gallery Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-semibold mb-8">Full Tour</h3>
          <ImageGallery images={images} />
        </div>

        <section className="flex mt-10 p-5 bg-zinc-100 rounded-xl">
          <FAQs faqs={FAQData} />
        </section>
      </div>
    </Layout>
  );
};

export default SerenityDetail;
